// API client — fetch wrapper with auth, i18n, and error normalization
const API_BASE = (import.meta as any).env?.VITE_API_BASE || '/api';

export type Locale = 'ar' | 'en';

let getToken: () => string | null = () => null;
let getLocale: () => Locale = () => 'ar';

let refreshFn: (() => Promise<string | null>) | null = null;
let onExpired: (() => void) | null = null;
export function configureApi(opts: { getToken: () => string | null; getLocale: () => Locale; refresh?: () => Promise<string | null>; onExpired?: () => void }) {
  getToken = opts.getToken;
  getLocale = opts.getLocale;
  refreshFn = opts.refresh || null;
  onExpired = opts.onExpired || null;
}
let refreshing: Promise<string | null> | null = null;

export class ApiError extends Error {
  status: number;
  code: string;
  details?: any;
  message_en: string;
  message_ar: string;
  constructor(status: number, code: string, message_en: string, message_ar: string, details?: any) {
    super(getLocale() === 'ar' ? (message_ar || message_en) : message_en);
    this.status = status;
    this.code = code;
    this.message_en = message_en;
    this.message_ar = message_ar;
    this.details = details;
  }
  localize(locale: Locale) {
    return locale === 'ar' ? this.message_ar : this.message_en;
  }
}

type RequestOpts = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: any;
  query?: Record<string, any>;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  signal?: AbortSignal;
};

export async function api<T = any>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method || 'GET';
  const url = new URL(API_BASE + path, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = {
    'Accept-Language': getLocale(),
    ...(opts.headers || {}),
  };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      body = opts.body;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
  }
  let res = await fetch(url.toString(), { method, headers, body, signal: opts.signal });
  if (res.status === 401 && token && refreshFn && !path.startsWith('/auth/')) {
    // access token expired -> try refresh once, then retry
    refreshing = refreshing || refreshFn().finally(() => { setTimeout(() => { refreshing = null; }, 0); });
    const nt = await refreshing;
    if (nt) { headers['Authorization'] = `Bearer ${nt}`; res = await fetch(url.toString(), { method, headers, body, signal: opts.signal }); }
    else if (onExpired) onExpired();
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = data?.error || {};
    throw new ApiError(res.status, err.code || 'unknown', err.message_en || res.statusText, err.message_ar || err.message_en || res.statusText, err.details);
  }
  return (data?.data ?? data) as T;
}

function safeJson(s: string) {
  try { return JSON.parse(s); } catch { return null; }
}

// Convenience helpers
export const apiGet = <T = any>(path: string, query?: any) => api<T>(path, { query });
export const apiPost = <T = any>(path: string, body?: any, opts?: Partial<RequestOpts>) => api<T>(path, { method: 'POST', body, ...opts });
export const apiPatch = <T = any>(path: string, body?: any) => api<T>(path, { method: 'PATCH', body });
export const apiDelete = <T = any>(path: string) => api<T>(path, { method: 'DELETE' });
export const currentToken = () => getToken();
